/**
 * 주문 관련 데이터를 중앙에서 관리하는 Pinia 스토어
 * 잔고, 주문내역, 미체결, 청산 데이터를 통합 관리
 */
import { calculatePositionProfitLoss, subtract, add } from '@template/utils';
import { useMetadataStore } from '../metadata/useMetadataStore';
import { useMarketDataStore } from '@template/stores';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
export const useOrderDataStore = defineStore('orderData', () => {
    const marketDataStore = useMarketDataStore();
    const metadataStore = useMetadataStore();
    // 잔고 데이터
    const positionStocks = ref([]);
    const positionStocksLoading = ref(false);
    const positionStocksError = ref(null);
    // 주문 내역 데이터
    const orderHistory = ref([]);
    const orderHistoryLoading = ref(false);
    const orderHistoryError = ref(null);
    // 미체결 주문 데이터
    const pendingOrders = ref([]);
    const pendingOrdersLoading = ref(false);
    const pendingOrdersError = ref(null);
    // 청산 내역 데이터
    const closeOutHistory = ref([]);
    const closeOutHistoryLoading = ref(false);
    const closeOutHistoryError = ref(null);
    // 선택된 계좌
    const selectedAccount = ref('');
    /** 주문 탭 내 캐시 유효 여부 (정정/청산 복귀 시 API 재호출 방지, 다른 탭 이탈 시 무효화) */
    const orderTabCacheValid = ref(false);
    /** 미체결 역전 감지용: 가장 오래된 미체결 주문 orderDateTime-orderNo (실시간 리스너에서 사용) */
    const oldestUnsettledOrderCache = ref(null);
    // 잔고 데이터 관리
    const setPositionStocks = (data) => {
        positionStocks.value = data;
    };
    /**
     * 잔고 데이터 추가
     * SELECT KEY: 종목코드, 포지션구분코드, 매입일시, 잔고번호
     * 새로운 잔고는 맨 앞에 삽입 (매입일시 내림차순 정렬 유지)
     */
    const addPositionStock = (data) => {
        const existingIndex = positionStocks.value.findIndex((item) => item.stockCd === data.stockCd &&
            item.positionCd === data.positionCd &&
            item.purchaseDateTime === data.purchaseDateTime &&
            item.positionNo === data.positionNo);
        if (existingIndex >= 0) {
            positionStocks.value[existingIndex] = data;
        }
        else {
            // 새로운 잔고는 맨 앞에 삽입 (매입일시 내림차순 정렬 유지)
            positionStocks.value.unshift(data);
        }
    };
    /**
     * 잔고 데이터 제거
     * SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
     */
    const removePositionStock = (stockCd, positionCd, purchaseDateTime, positionNo) => {
        const index = positionStocks.value.findIndex((item) => item.stockCd === stockCd &&
            item.positionCd === positionCd &&
            (purchaseDateTime === undefined || item.purchaseDateTime === purchaseDateTime) &&
            (positionNo === undefined || item.positionNo === positionNo));
        if (index >= 0) {
            positionStocks.value.splice(index, 1);
        }
    };
    /**
     * 잔고 데이터 업데이트
     * SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
     */
    const updatePositionStock = (stockCd, positionCd, updates, purchaseDateTime, positionNo) => {
        // updates에 purchaseDateTime이나 positionNo가 있으면 그것을 사용
        const targetPurchaseDateTime = updates.purchaseDateTime ?? purchaseDateTime;
        const targetPositionNo = updates.positionNo ?? positionNo;
        const index = positionStocks.value.findIndex((item) => item.stockCd === stockCd &&
            item.positionCd === positionCd &&
            (targetPurchaseDateTime === undefined ||
                item.purchaseDateTime === targetPurchaseDateTime) &&
            (targetPositionNo === undefined || item.positionNo === targetPositionNo));
        if (index >= 0) {
            positionStocks.value[index] = { ...positionStocks.value[index], ...updates };
        }
        else if (targetPurchaseDateTime &&
            targetPositionNo &&
            updates.accountBookQuantity !== undefined) {
            // 잔고가 없는데 업데이트하려는 경우, 새로 추가 (장부수량이 있는 경우만)
            // 이는 실시간 메시지에서 새로운 잔고가 생성되는 경우를 처리
            // 새로운 잔고는 맨 앞에 삽입 (매입일시 내림차순 정렬 유지)
            const newPosition = {
                stockCd,
                positionCd: positionCd,
                purchaseDateTime: targetPurchaseDateTime,
                positionNo: targetPositionNo,
                accountBookQuantity: updates.accountBookQuantity,
                liquidationPossibleQuantity: updates.liquidationPossibleQuantity ?? 0,
                accountBookPrice: updates.accountBookPrice ?? 0,
                currentPrice: updates.currentPrice ?? 0,
                assessmentProfitLoss: updates.assessmentProfitLoss ?? 0,
                tradeCurrencyCd: updates.tradeCurrencyCd ?? '',
                stockGroupCd: updates.stockGroupCd ?? 'ALL',
            };
            positionStocks.value.unshift(newPosition);
        }
    };
    // 주문 내역 데이터 관리
    const setOrderHistory = (data) => {
        orderHistory.value = data;
    };
    const addOrderHistory = (data) => {
        const existingIndex = orderHistory.value.findIndex((item) => item.orderNo === data.orderNo);
        if (existingIndex >= 0) {
            orderHistory.value[existingIndex] = data;
        }
        else {
            orderHistory.value.unshift(data); // 최신 주문을 맨 앞에 추가
        }
    };
    const updateOrderHistory = (orderNo, updates) => {
        const index = orderHistory.value.findIndex((item) => item.orderNo === orderNo);
        if (index >= 0) {
            orderHistory.value[index] = { ...orderHistory.value[index], ...updates };
        }
    };
    // 미체결 주문 데이터 관리
    const setPendingOrders = (data) => {
        pendingOrders.value = data;
    };
    const addPendingOrder = (data) => {
        const existingIndex = pendingOrders.value.findIndex((item) => item.orderNo === data.orderNo);
        if (existingIndex >= 0) {
            pendingOrders.value[existingIndex] = data;
        }
        else {
            pendingOrders.value.unshift(data); // 최신 주문을 맨 앞에 추가
        }
    };
    const removePendingOrder = (orderNo) => {
        const index = pendingOrders.value.findIndex((item) => item.orderNo === orderNo);
        if (index >= 0) {
            pendingOrders.value.splice(index, 1);
        }
    };
    const updatePendingOrder = (orderNo, updates) => {
        const index = pendingOrders.value.findIndex((item) => item.orderNo === orderNo);
        if (index >= 0) {
            pendingOrders.value[index] = { ...pendingOrders.value[index], ...updates };
        }
    };
    // 청산 내역 데이터 관리
    const setCloseOutHistory = (data) => {
        closeOutHistory.value = data;
    };
    const addCloseOutHistory = (data) => {
        const existingIndex = closeOutHistory.value.findIndex((item) => item.orderNo === data.orderNo);
        if (existingIndex >= 0) {
            closeOutHistory.value[existingIndex] = data;
        }
        else {
            closeOutHistory.value.unshift(data); // 최신 청산을 맨 앞에 추가
        }
    };
    // 로딩 상태 관리
    const setPositionStocksLoading = (loading) => {
        positionStocksLoading.value = loading;
    };
    const setOrderHistoryLoading = (loading) => {
        orderHistoryLoading.value = loading;
    };
    const setPendingOrdersLoading = (loading) => {
        pendingOrdersLoading.value = loading;
    };
    const setCloseOutHistoryLoading = (loading) => {
        closeOutHistoryLoading.value = loading;
    };
    // 에러 상태 관리
    const setPositionStocksError = (error) => {
        positionStocksError.value = error;
    };
    const setOrderHistoryError = (error) => {
        orderHistoryError.value = error;
    };
    const setPendingOrdersError = (error) => {
        pendingOrdersError.value = error;
    };
    const setCloseOutHistoryError = (error) => {
        closeOutHistoryError.value = error;
    };
    // 선택된 계좌 관리
    const setSelectedAccount = (account) => {
        selectedAccount.value = account;
    };
    /** 주문 탭 캐시 유효 플래그 설정 (다른 탭 이동 시 false로 무효화) */
    const setOrderTabCacheValid = (valid) => {
        orderTabCacheValid.value = valid;
    };
    /** 미체결 가장 오래된 주문 캐시 설정 (실시간 역전 감지용) */
    const setOldestUnsettledOrderCache = (value) => {
        oldestUnsettledOrderCache.value = value;
    };
    // 계산된 속성들
    const totalPositionValue = computed(() => {
        return positionStocks.value.reduce((total, position) => {
            return total + position.currentPrice * position.accountBookQuantity;
        }, 0);
    });
    /**
     * 거래 통화 → USD 환율 조회
     * - 우선 직접환율(TRDUSD)을 시도
     * - 없으면 역환율(USDTRD)을 역수로 변환
     * - 둘 다 없으면 교차환율(예: TRDEUR * EURUSD) 시도
     * - 모든 시도가 실패하면 1 반환
     *
     * @param tradeCurrencyCd - 거래 통화 코드 (예: JPY, EUR, GBP)
     * @returns number USD 기준 환율(1 TRD 당 USD)
     */
    const getCurrencyRate = (tradeCurrencyCd) => {
        if (!tradeCurrencyCd || tradeCurrencyCd === 'USD') {
            return 1;
        }
        const getValidRate = (symbol) => {
            const symbolData = marketDataStore.getSymbolDataBySymbol(symbol);
            const price = symbolData?.sellPrice ?? 0;
            return price > 0 ? price : null;
        };
        // 1) 직접환율
        // TRDUSD
        const directPairSymbol = `${tradeCurrencyCd}USD`;
        const directRate = getValidRate(directPairSymbol);
        if (directRate) {
            return directRate;
        }
        // 2) 역환율
        // USDTRD => 역수
        const inversePairSymbol = `USD${tradeCurrencyCd}`;
        const inverseRate = getValidRate(inversePairSymbol);
        if (inverseRate) {
            const invertedRate = 1 / inverseRate;
            if (Number.isFinite(invertedRate) && invertedRate > 0) {
                return invertedRate;
            }
        }
        // 3) 교차환율
        // TRD=>BASE * BASE=>USD (BASE 후보: EUR, JPY, GBP)
        const CROSS_BASES = ['EUR', 'JPY', 'GBP'];
        for (const base of CROSS_BASES) {
            // TRD=>BASE
            const directToBase = getValidRate(`${tradeCurrencyCd}${base}`);
            const inverseToBase = getValidRate(`${base}${tradeCurrencyCd}`);
            const trdToBase = directToBase ?? (inverseToBase ? (1 / inverseToBase > 0 ? 1 / inverseToBase : null) : null);
            // BASE=>USD
            const baseToUsdDirect = getValidRate(`${base}USD`);
            const baseToUsdInverse = getValidRate(`USD${base}`);
            const baseToUsd = baseToUsdDirect ??
                (baseToUsdInverse ? (1 / baseToUsdInverse > 0 ? 1 / baseToUsdInverse : null) : null);
            if (trdToBase && baseToUsd) {
                const crossed = trdToBase * baseToUsd;
                if (Number.isFinite(crossed) && crossed > 0) {
                    return crossed;
                }
            }
        }
        return 1;
    };
    /**
     * 포지션 타입에 따라 적절한 시세 가격을 반환
     * LONG: 매도가(sellPrice), SHORT: 매수가(buyPrice)
     */
    const resolveMarketPriceForPosition = (stockCd, positionCd) => {
        const data = marketDataStore.getSymbolDataBySymbol(stockCd);
        if (!data) {
            return undefined;
        }
        const isLong = positionCd === 'LONG';
        const price = isLong ? data.sellPrice : data.buyPrice;
        return price;
    };
    /**
     * Trailing Stop 손실제한배리어가격 계산
     *
     * - 현재가 = (포지션구분코드 === LONG) ? 매도 1호가 : 매수 1호가
     * - 손실추적증감 = 현재가 - 최초손실추적가격
     * - 손실제한배리어증감 = 손실제한배리어가격 - 최초손실제한가격
     * - 손실제한배리어가격 = 최초손실제한가격 + 손실추적증감으로 갱신
     *   - 포지션 구분 코드 LONG and 손실제한배리어증감 < 손실추적증감
     *   - 포지션 구분 코드 SHORT and 손실제한배리어증감 > 손실추적증감
     *
     * @param pendingOrder - 미체결 주문 데이터
     * @returns 계산된 손실제한배리어가격. Trailing Stop이 아니거나 계산 불가능하면 기존 값 반환
     */
    const calculateTrailingStopLossCutPrice = (pendingOrder) => {
        // Trailing Stop이 설정되지 않은 경우 기존 값 반환
        if (pendingOrder.isLossTracing !== 'Y') {
            return pendingOrder.lossCutBarrierPrice;
        }
        // 최초값이 없으면 계산 불가능
        if (!pendingOrder.firstLossTrackingPrice ||
            !pendingOrder.firstLossCutPrice ||
            pendingOrder.firstLossTrackingPrice <= 0 ||
            pendingOrder.firstLossCutPrice <= 0) {
            return pendingOrder.lossCutBarrierPrice;
        }
        // 현재가 조회
        const currentPrice = resolveMarketPriceForPosition(pendingOrder.stockCd, pendingOrder.positionCd);
        if (!currentPrice || currentPrice <= 0) {
            return pendingOrder.lossCutBarrierPrice;
        }
        // 손실추적증감 = 현재가 - 최초손실추적가격 (부동소수점 오차 방지를 위해 decimal 사용)
        const lossTrackingIncrement = subtract(currentPrice, pendingOrder.firstLossTrackingPrice);
        // 손실제한배리어증감 = 손실제한배리어가격 - 최초손실제한가격 (부동소수점 오차 방지를 위해 decimal 사용)
        const lossCutBarrierIncrement = subtract(pendingOrder.lossCutBarrierPrice, pendingOrder.firstLossCutPrice);
        // 계산된 손실제한배리어가격 = 최초손실제한가격 + 손실추적증감 (부동소수점 오차 방지를 위해 decimal 사용)
        const calculatedLossCutPrice = add(pendingOrder.firstLossCutPrice, lossTrackingIncrement);
        // Trailing Stop 업데이트 조건:
        // 주석에 명시된 원래 로직에 따라 이익 보호 방향으로만 업데이트
        // - LONG: lossCutBarrierIncrement < lossTrackingIncrement (손실제한가격이 손실추적가격보다 덜 상승했을 때)
        // - SHORT: lossCutBarrierIncrement > lossTrackingIncrement (손실제한가격이 손실추적가격보다 덜 하락했을 때)
        const isLong = pendingOrder.positionCd === 'LONG';
        const shouldUpdate = (isLong && lossCutBarrierIncrement < lossTrackingIncrement) ||
            (!isLong && lossCutBarrierIncrement > lossTrackingIncrement);
        if (shouldUpdate && calculatedLossCutPrice > 0) {
            return calculatedLossCutPrice;
        }
        // 조건에 맞지 않으면 기존 값 반환
        return pendingOrder.lossCutBarrierPrice;
    };
    /**
     * 각 포지션의 평가손익을 계산하는 함수
     * OrderBalanceTable의 각 row와 동일한 로직 사용
     * @param position - 포지션 데이터
     * @returns 계산된 평가손익
     */
    const calculatePositionProfitLossValue = (position) => {
        const resolvedPrice = resolveMarketPriceForPosition(position.stockCd, position.positionCd);
        const apiAssessmentProfitLoss = position.assessmentProfitLoss || 0;
        if (resolvedPrice && resolvedPrice > 0) {
            // 실시간 가격이 있으면 계산
            const metadata = metadataStore.getMetadata(position.stockCd);
            const currencyRate = getCurrencyRate(position.tradeCurrencyCd);
            return calculatePositionProfitLoss({
                currentPrice: resolvedPrice,
                bookPrice: position.accountBookPrice ?? 0,
                quantity: position.accountBookQuantity ?? 0,
                positionCd: position.positionCd,
                metadata,
                currencyRate,
            });
        }
        else {
            // 실시간 가격이 없으면 WebSocket에서 받은 값 사용
            return apiAssessmentProfitLoss;
        }
    };
    /**
     * 각 포지션의 평가손익을 계산하여 Map으로 저장
     * 키: stockCd_positionCd_purchaseDateTime_positionNo
     */
    const positionProfitLossMap = computed(() => {
        const map = new Map();
        positionStocks.value.forEach((position) => {
            const key = `${position.stockCd}_${position.positionCd}_${position.purchaseDateTime}_${position.positionNo}`;
            map.set(key, calculatePositionProfitLossValue(position));
        });
        return map;
    });
    /**
     * 총 평가손익: 각 포지션의 평가손익 합계
     * 중복 계산을 피하기 위해 positionProfitLossMap 사용
     */
    const totalProfitLoss = computed(() => {
        return Array.from(positionProfitLossMap.value.values()).reduce((total, profitLoss) => total + profitLoss, 0);
    });
    const pendingOrdersCount = computed(() => {
        return pendingOrders.value.length;
    });
    const activePositionsCount = computed(() => {
        return positionStocks.value.length;
    });
    /**
     * 검색어에 따라 필터링된 잔고 데이터
     * @param searchKeyword - 검색 키워드
     */
    const getFilteredPositionStocks = (searchKeyword) => {
        if (!searchKeyword) {
            return positionStocks.value;
        }
        return positionStocks.value.filter((item) => item.stockCd.toLowerCase().includes(searchKeyword.toLowerCase()));
    };
    // 데이터 초기화
    const clearAllData = () => {
        positionStocks.value = [];
        orderHistory.value = [];
        pendingOrders.value = [];
        closeOutHistory.value = [];
        positionStocksError.value = null;
        orderHistoryError.value = null;
        pendingOrdersError.value = null;
        closeOutHistoryError.value = null;
    };
    // 특정 계좌의 데이터만 초기화
    const clearAccountData = (account) => {
        if (selectedAccount.value === account) {
            clearAllData();
        }
    };
    return {
        // 상태
        positionStocks,
        positionStocksLoading,
        positionStocksError,
        orderHistory,
        orderHistoryLoading,
        orderHistoryError,
        pendingOrders,
        pendingOrdersLoading,
        pendingOrdersError,
        closeOutHistory,
        closeOutHistoryLoading,
        closeOutHistoryError,
        selectedAccount,
        orderTabCacheValid,
        oldestUnsettledOrderCache,
        // 계산된 속성
        totalPositionValue,
        totalProfitLoss,
        positionProfitLossMap,
        pendingOrdersCount,
        activePositionsCount,
        // 잔고 데이터 액션
        setPositionStocks,
        addPositionStock,
        removePositionStock,
        updatePositionStock,
        // 주문 내역 액션
        setOrderHistory,
        addOrderHistory,
        updateOrderHistory,
        // 미체결 주문 액션
        setPendingOrders,
        addPendingOrder,
        removePendingOrder,
        updatePendingOrder,
        // 청산 내역 액션
        setCloseOutHistory,
        addCloseOutHistory,
        // 로딩 상태 액션
        setPositionStocksLoading,
        setOrderHistoryLoading,
        setPendingOrdersLoading,
        setCloseOutHistoryLoading,
        // 에러 상태 액션
        setPositionStocksError,
        setOrderHistoryError,
        setPendingOrdersError,
        setCloseOutHistoryError,
        // 계좌 관리 액션
        setSelectedAccount,
        setOrderTabCacheValid,
        setOldestUnsettledOrderCache,
        // 유틸리티 액션
        clearAllData,
        clearAccountData,
        getCurrencyRate,
        getFilteredPositionStocks,
        calculatePositionProfitLossValue,
        resolveMarketPriceForPosition,
        calculateTrailingStopLossCutPrice,
    };
});
//# sourceMappingURL=useOrderDataStore.js.map